import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
	initialValues,
	getReportDetailsSchema,
	ReportTypes
} from '../../constants/reports/report-form';
import { REPORT_FIELDS } from '../../constants/reports/report-fields';
import { useTranslation } from 'react-i18next';
import { FormInput } from '../merchants/FormInput';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { ButtonComponent } from '../ButtonComponent';
import InfiniteScroll from 'react-infinite-scroll-component';
import './Reports.scss';
import { debounce } from 'lodash';
import { fetchMerchantsListReport } from '../../store/merchnts/actions/merchant';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { SectionWrapper } from '../SectionWrapper';
import { setReport } from '../../store/order/actions/reports';
import { useOrdersContext } from '../../store/order/order-context';

export const Reports = (): JSX.Element => {
	const { t } = useTranslation();
	const formik = useFormik<ReportTypes>({
		initialValues,
		validationSchema: getReportDetailsSchema(t),
		onSubmit: () => void 0
	});
	const { merchantState: { merchantsListReport }, dispatch } = useMerchants();
	const { orderContextState: { isSendingReport, reportTypesList }, dispatch: orderDispatch } = useOrdersContext();
	const [searchValue, setSearchValue] = useState<string>('');
	const [currentPage, setCurrentPage] = useState(1);
	const [wasSearched, setWasSearched] = useState(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const loadNextPage = async (): Promise<void> => {
		const nextPage = currentPage + 1;
		await fetchMerchantsListReport(dispatch, nextPage, merchantsListReport, searchValue);
		setCurrentPage(nextPage);
	};

	useEffect(() => {
		if (Boolean(searchValue) === wasSearched) {
			void fetchMerchantsListReport(dispatch, currentPage, merchantsListReport, searchValue);
		} else {
			setWasSearched(prev => !prev);
			void fetchMerchantsListReport(dispatch, 1, [], searchValue);
			setCurrentPage(1);
		}
	}, [searchValue]);

	const renderMenu = (menu): JSX.Element => {
		return (
			<InfiniteScroll
				dataLength={merchantsListReport.length}
				next={loadNextPage}
				hasMore={true}
				loader={<div></div>}
				height={320}
			>
				{menu}
			</InfiniteScroll>
		);
	};

	const setSearch = debounce((keyword) => {
		if (searchValue !== keyword) {
			setCurrentPage(1);
		}
		setSearchValue(keyword);
	}, 1000);

	const searchBy = (keyword: string): boolean => {
		setSearch(keyword);
		return Boolean(merchantsListReport.length);
	};
	const INPUTS = REPORT_FIELDS({ t, merchantsListReport, renderMenu, searchBy, reportTypesList });

	const successModalOpen = (text: string): void => {
		setModalState({
			isOpen: true,
			modalTitle: t('MENU.REPORTS'),
			modalText: text,
			onConfirm: closeModal,
			confirmButtonText: 'Ok',
			minHeight: 100
		});
	};

	return (
		<>
		<SectionWrapper
			title={t('MENU.REPORTS')}
			sectionAdditionalClass="mb-10"
			isCardBody
		>
					<form>
						<div className="row">
							{
								INPUTS.map((item): JSX.Element => {
									return (
										<FormInput
											{...item}
											formik={formik}
											key={item.key}
										/>
									);
								})
							}
						</div>
						<div className="text-end mt-10">
							<ButtonComponent
								buttonName={t('REPORTS.BTN_NAME')}
								onButtonClick={(): Promise<void> => !isSendingReport && setReport(orderDispatch, formik, successModalOpen)}
								loading={isSendingReport}
								additionalClass="btn-sm w-115px"
							/>
						</div>
					</form>
		</SectionWrapper>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
		</>
	);
};
